'use client';

import { useTranslation } from 'react-i18next';
import { useElevationControl } from '@web-store-platforms/providers/elevation';
import { useAuthBoot } from '../../context/AuthBootContext';
import { cn } from '@/lib/utils/cn';
import { DEFAULT_TEXT_COLOR, SHAKE_ANIMATION_DURATION_MS } from '../../constants';
import { FeatureFlags } from '@/lib/consts/featureFlags';
import { isProductMadnessLoginDesign } from '@/lib/consts/productMadness';
import { useLoginDisplay } from '../../hooks/useLoginDisplay';
import { useConsentContext } from '../../context/ConsentContext';
import { SimpleTerms } from './SimpleTerms';
import { AdvancedTerms } from './AdvancedTerms';

const TERMS_PLATE_CLASS_NAME =
  'w-full rounded-[4px] bg-black/60 p-4 text-left text-white text-[12px] leading-[14px]';

interface ITermsBlockProps {
  hasConsent?: boolean;
  onConsentChange?: (isConsented: boolean) => void;
  isConsentRequired?: boolean;
  sanitizedConsentHtml?: string;
  textColor?: string;
  textSizeClassName?: string;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  withPlate?: boolean;
}

export const TermsBlock = ({
  hasConsent = false,
  onConsentChange,
  isConsentRequired = false,
  sanitizedConsentHtml,
  textColor,
  textSizeClassName = 'text-[16px]',
  privacyPolicyUrl,
  termsAndConditionsUrl,
  withPlate = true,
}: ITermsBlockProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { authBoot } = useAuthBoot();
  const { displayTermsBlock } = useLoginDisplay();
  const zIndex = useElevationControl();
  const { isConsentV2, isAlertShaking } = useConsentContext();
  const showPlate = withPlate && isConsentV2;
  const isShaking = isConsentV2 && isAlertShaking;

  const resolvedTextColor = showPlate
    ? DEFAULT_TEXT_COLOR
    : textColor || authBoot.storeTheme.login?.textColor || DEFAULT_TEXT_COLOR;
  const isAdvancedTextFeatureEnabled =
    authBoot.featureFlags?.[FeatureFlags.DASHBOARD_NEW_PRIVACY_RICH_TEXT] ?? false;
  const showAgeDisclaimer = isProductMadnessLoginDesign(authBoot.storeTheme.general?.loginDesignId);

  if (!displayTermsBlock) {
    return null;
  }

  const layoutClassName = showPlate
    ? cn('login-terms-text max-w-[400px] mt-5 relative break-words', TERMS_PLATE_CLASS_NAME)
    : cn('login-terms-text max-w-[400px] mt-5 relative text-center break-words', textSizeClassName);
  const termsClassName = cn(layoutClassName, isShaking ? 'animate-login-consent-shake' : '');
  const termsStyle = isShaking
    ? { zIndex, color: resolvedTextColor, animationDuration: `${SHAKE_ANIMATION_DURATION_MS}ms` }
    : { zIndex, color: resolvedTextColor };

  return (
    <div
      className={termsClassName}
      data-plate={showPlate ? 'true' : undefined}
      style={termsStyle}
      data-testid="login-page-terms-block"
    >
      {isAdvancedTextFeatureEnabled ? (
        <AdvancedTerms
          textColor={resolvedTextColor}
          hasConsent={hasConsent}
          onConsentChange={onConsentChange}
          isConsentRequired={isConsentRequired}
          sanitizedHtml={sanitizedConsentHtml ?? ''}
          isPlateStyle={showPlate}
        />
      ) : (
        <SimpleTerms
          textColor={resolvedTextColor}
          privacyPolicyUrl={privacyPolicyUrl}
          termsAndConditionsUrl={termsAndConditionsUrl}
        />
      )}
      {showAgeDisclaimer && (
        <div className="mt-4" data-testid="login-age-disclaimer">
          {t('login.termsText')}
        </div>
      )}
    </div>
  );
};
